import { useEffect, useMemo, useState } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  type Document,
  type Manifest,
  type Reference,
  useGetAuthoringComponentQuery,
  useLazyGetAuthoringComponentQuery,
  useListComponentsQuery,
} from '../../api/authoring'
import { payloadId, shown } from '../../api/useIntent'
import { typeProse, fieldsSchema } from '../../model/manifest'
import { collectionTitle, dividingFields, keyAndLabel, recordTypeOf, type DividingField } from '../../model/records'
import { mintEdgeId } from '../../model/references'
import { orderedProperties, resolve, type Schema } from '../../model/schema'
import type { WebsiteMenuFilter, WebsiteRoute } from '../../model/website'
import { MenuTags } from './MenuTags'

/**
 * The one routing node that produces a page for every record in a
 * Collection.
 *
 * **It starts from the Collection**, because that is what an agency has in
 * mind: "each album gets a page". The record type follows from it, and
 * from the type follow the route's id and binding name, the address each
 * page gets, the page patterns that can draw one, and the fields a menu
 * can show or filter on. Nothing the dialog can work out is asked for, and
 * nothing it asks for is typed as binding syntax.
 *
 * A generated route is deliberately not a variation of `RouteDialog`: it
 * serves no Page placement, and its source and pattern are Website
 * references. Keeping those facts together prevents a picker from looking
 * like it chose a page when it actually chose the recipe for one.
 */
export function GeneratedRouteDialog({
  tenant,
  manifest,
  references,
  sourceTypes,
  patternTypes,
  under,
  parentPath,
  position,
  menuOptions,
  route,
  onClose,
  onSend,
  busy,
}: {
  tenant: string
  manifest: Manifest | undefined
  references: Reference[]
  sourceTypes: string[]
  patternTypes: string[]
  under: string
  /** The URL path of the route this one sits under, for the address preview. */
  parentPath: string
  position: number
  /** Every menu the site already uses, offered before a new one is typed. */
  menuOptions: string[]
  route?: WebsiteRoute
  onClose: () => void
  onSend: (name: string, payload: Record<string, unknown>) => Promise<unknown>
  busy: boolean
}) {
  const adding = route === undefined
  const edge = (id: string | undefined, name: string) =>
    references.find((reference) => reference.edgeId === id && reference.name === name)

  const heldSource = edge(route?.source, 'routeSource')?.id ?? ''
  const heldPattern = edge(route?.pattern, 'routePattern')?.id ?? ''
  const [source, setSource] = useState(heldSource)
  const [pattern, setPattern] = useState(heldPattern)
  const [id, setID] = useState(route?.id ?? '')
  const [param, setParam] = useState(route?.param ?? '')
  const [title, setTitle] = useState(route?.title ?? '')
  const [description, setDescription] = useState(route?.description ?? '')
  const [menus, setMenus] = useState<string[]>(route?.menus ?? [])
  const [filters, setFilters] = useState<WebsiteMenuFilter[]>(route?.menuFilters ?? [])

  const collections = useListComponentsQuery({ tenant, type: sourceTypes[0] ?? '', limit: 100 }, { skip: !sourceTypes[0] })
  const collection = useGetAuthoringComponentQuery({ tenant, id: source }, { skip: !source })
  const recordType = recordTypeOf(manifest, source, collection.data)
  const { key, label } = keyAndLabel(manifest, recordType ?? '')
  const patterns = usePagePatterns(tenant, patternTypes[0], recordType)
  // A route's Collection and pattern change in place, each by retargeting
  // the Website's edge the route names, so the route keeps its id and its
  // pages keep theirs. A changed pair still has to draw a page for the
  // Collection's records.
  const retargeting = !adding && (source !== heldSource || pattern !== heldPattern)
  const patternFits = patterns.loading || patterns.compatible.some((candidate) => candidate.id === pattern)

  // What the record type decides, filled in as soon as it is known and
  // left alone once the author has changed it.
  useEffect(() => {
    if (!adding || !recordType) return
    const local = recordType.slice(recordType.indexOf('/') + 1).replace(/[^a-z0-9]+(.)?/g, (_m, next: string | undefined) => (next ?? '').toUpperCase())
    setID((current) => current || local)
    setParam((current) => current || local)
    setTitle((current) => current || (label ? `$${local}.${label}` : ''))
  }, [adding, recordType, label])
  useEffect(() => {
    if (adding && !pattern && patterns.compatible.length === 1) setPattern(patterns.compatible[0].id)
  }, [adding, pattern, patterns.compatible])

  const fields = recordType ? fieldsSchema(manifest, recordType) : undefined
  const words = textFields(fields)
  const dividers = dividingFields(fields)
  const binding = (field: string) => (field ? `$${param}.${field}` : '')
  const fieldOf = (expression: string) => expression.replace(`$${param}.`, '')

  const problems: string[] = []
  if (source && recordType === undefined && collection.data) {
    problems.push('This Collection holds no records yet and no record type names it, so there is nothing to give pages to.')
  }
  if (recordType && !key) {
    problems.push(`${typeProse(manifest, recordType).title} has no route key. Choose one in its type designer; until then no record has an address.`)
  }
  if (adding && recordType && !patterns.loading && patterns.compatible.length === 0) {
    problems.push(`No page pattern draws a Page for ${typeProse(manifest, recordType).title.toLowerCase()}. Write one in the pattern designer: a Page at the root, whose input accepts ${recordType}.`)
  }
  if (retargeting && !patternFits) {
    problems.push(`This page pattern does not draw a page for ${recordType ? typeProse(manifest, recordType).title.toLowerCase() : 'these records'}. Choose one that does.`)
  }
  const complete = Boolean(id.trim() && param.trim() && source && pattern && recordType && key && (!retargeting || patternFits))

  const submit = async () => {
    if (!complete) return
    const generated: WebsiteRoute = {
      id: id.trim(),
      param: param.trim(),
      source: route?.source,
      pattern: route?.pattern,
      title: title || undefined,
      description: description || undefined,
      menus,
      menuFilters: filters.filter((filter) => menus.includes(filter.tag) && filter.where.field && filter.where.equals !== ''),
    }
    if (adding) {
      const taken = new Set(references.map((reference) => reference.edgeId))
      const sourceEdge = mintEdgeId(`route-source-${generated.id}`, taken)
      taken.add(sourceEdge)
      const patternEdge = mintEdgeId(`route-pattern-${generated.id}`, taken)
      generated.source = sourceEdge
      generated.pattern = patternEdge
      await onSend('website.generated_route_created', {
        under: under || undefined,
        position,
        route: generated,
        source: { edgeId: sourceEdge, name: 'routeSource', id: payloadId(tenant, source) },
        pattern: { edgeId: patternEdge, name: 'routePattern', id: payloadId(tenant, pattern) },
      })
    } else {
      if (source !== heldSource && route?.source) {
        await onSend('component.reference_retargeted', { edgeId: route.source, id: payloadId(tenant, source) })
      }
      if (pattern !== heldPattern && route?.pattern) {
        await onSend('component.reference_retargeted', { edgeId: route.pattern, id: payloadId(tenant, pattern) })
      }
      await onSend('website.generated_route_updated', { route: generated })
    }
    onClose()
  }

  const addressPreview = `${parentPath === '/' ? '' : parentPath}/{${key ?? 'key'}}`

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{adding ? 'Give each record a page' : 'Edit the pages for each record'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label="Records"
            value={source}
            disabled={busy}
            onChange={(event) => setSource(event.target.value)}
            helperText="The Collection whose records each get a page."
          >
            {source && !(collections.data?.components ?? []).some((component) => component.id === source) && (
              <MenuItem value={source}>{collectionTitle(manifest, source, collection.data?.label)}</MenuItem>
            )}
            {(collections.data?.components ?? []).map((component) => (
              <MenuItem key={component.id} value={component.id}>
                {collectionTitle(manifest, component.id, component.label)}
              </MenuItem>
            ))}
          </TextField>

          {recordType && (
            <Typography variant="body2" color="text.secondary">
              Each {typeProse(manifest, recordType).title.toLowerCase()} gets a page at{' '}
              <strong style={{ fontFamily: 'ui-monospace, monospace' }}>{addressPreview}</strong>.
            </Typography>
          )}

          <TextField
            select
            label="Page pattern"
            value={pattern}
            disabled={busy || !recordType}
            onChange={(event) => setPattern(event.target.value)}
            helperText={
              patterns.others > 0
                ? `Only patterns that draw a Page for this record type are offered; ${patterns.others} other ${patterns.others === 1 ? 'pattern does' : 'patterns do'} not.`
                : 'What each page is drawn from.'
            }
          >
            {pattern && !patterns.compatible.some((candidate) => candidate.id === pattern) && (
              <MenuItem value={pattern}>{patterns.name(pattern) ?? pattern}</MenuItem>
            )}
            {patterns.compatible.map((candidate) => (
              <MenuItem key={candidate.id} value={candidate.id}>
                {candidate.label ?? candidate.id}
              </MenuItem>
            ))}
          </TextField>

          {problems.map((problem) => (
            <Alert key={problem} severity="warning">
              {problem}
            </Alert>
          ))}

          <Typography variant="subtitle2">In menus</Typography>
          <MenuTags
            value={menus}
            options={menuOptions}
            onChange={setMenus}
            disabled={busy}
            helperText="Every page this route gives joins these menus. Leave it empty to keep them out of menus."
          />
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Menu title"
              value={fieldOf(title)}
              disabled={busy || !recordType}
              onChange={(event) => setTitle(binding(event.target.value))}
              helperText="The field a menu shows."
              sx={{ flex: 1 }}
            >
              <MenuItem value="">(none)</MenuItem>
              {words.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Menu description"
              value={fieldOf(description)}
              disabled={busy || !recordType}
              onChange={(event) => setDescription(binding(event.target.value))}
              helperText="Shown under it, where a menu has room."
              sx={{ flex: 1 }}
            >
              <MenuItem value="">(none)</MenuItem>
              {words.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Filters
            filters={filters}
            setFilters={setFilters}
            tags={menus}
            fields={dividers}
            disabled={busy}
          />

          <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">Names used by the model</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label="Route id"
                  value={id}
                  disabled={busy || !adding}
                  onChange={(event) => setID(event.target.value)}
                  helperText="Unique across this site. A page keeps its identity through it when its key changes."
                />
                <TextField
                  label="Binding name"
                  value={param}
                  disabled={busy}
                  onChange={(event) => setParam(event.target.value)}
                  helperText="What the page pattern calls the record, as in $album.title."
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={busy || !complete} onClick={() => submit().catch(shown)}>
          {adding ? 'Give them pages' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * The page patterns that can draw a page for this record type: a Page at
 * the root, and an input accepting the type. Every pattern is read to
 * find them, which a tenant's handful makes affordable.
 */
function usePagePatterns(tenant: string, patternType: string | undefined, recordType: string | undefined) {
  const listing = useListComponentsQuery({ tenant, type: patternType ?? '', limit: 100 }, { skip: !patternType })
  const [read] = useLazyGetAuthoringComponentQuery()
  const [documents, setDocuments] = useState<Document[] | undefined>()
  const summaries = listing.data?.components

  useEffect(() => {
    if (!summaries) return
    let current = true
    Promise.all(summaries.map((summary) => read({ tenant, id: summary.id }, true).unwrap()))
      .then((all) => current && setDocuments(all))
      .catch(() => current && setDocuments([]))
    return () => {
      current = false
    }
  }, [summaries, read, tenant])

  return useMemo(() => {
    const compatible = (documents ?? []).filter((document) => {
      const fields = document.fields as { input?: { accepts?: string }; tree?: { type?: string } } | undefined
      return fields?.input?.accepts === recordType && fields?.tree?.type === 'Page'
    })
    return {
      loading: documents === undefined,
      compatible,
      others: (documents?.length ?? 0) - compatible.length,
      name: (id: string) => summaries?.find((summary) => summary.id === id)?.label,
    }
  }, [documents, recordType, summaries])
}

/** The record's own words: its text fields, which a menu can show. */
function textFields(fields: Schema | undefined): string[] {
  if (!fields) return []
  return orderedProperties(fields, fields)
    .filter((property) => resolve(property.schema, fields)?.type === 'string' && !resolve(property.schema, fields)?.enum)
    .map((property) => property.name)
}

function Filters({
  filters,
  setFilters,
  tags,
  fields,
  disabled,
}: {
  filters: WebsiteMenuFilter[]
  setFilters: (next: WebsiteMenuFilter[]) => void
  tags: string[]
  fields: DividingField[]
  disabled: boolean
}) {
  const replace = (index: number, next: WebsiteMenuFilter) => setFilters(filters.map((entry, at) => (at === index ? next : entry)))
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Only some in a menu</Typography>
      <Typography variant="caption" color="text.secondary">
        A menu can take just the records whose yes/no or choice field has one value, such as the featured ones in the
        footer.
      </Typography>
      {filters.map((filter, index) => {
        const field = fields.find((entry) => entry.name === filter.where.field)
        return (
          <Stack key={index} direction="row" spacing={1}>
            <TextField
              select
              label="Menu"
              size="small"
              value={tags.includes(filter.tag) ? filter.tag : ''}
              disabled={disabled}
              onChange={(event) => replace(index, { ...filter, tag: event.target.value })}
              sx={{ minWidth: 120 }}
            >
              {tags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {tag}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Where"
              size="small"
              value={field ? field.name : ''}
              disabled={disabled}
              onChange={(event) => replace(index, { ...filter, where: { field: event.target.value, equals: '' } })}
              sx={{ minWidth: 140 }}
            >
              {fields.map((entry) => (
                <MenuItem key={entry.name} value={entry.name}>
                  {entry.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Is"
              size="small"
              value={field?.values.some((value) => value === filter.where.equals) ? String(filter.where.equals) : ''}
              disabled={disabled || !field}
              onChange={(event) =>
                replace(index, {
                  ...filter,
                  where: {
                    ...filter.where,
                    equals: field?.values.find((value) => String(value) === event.target.value) ?? '',
                  },
                })
              }
              sx={{ minWidth: 120 }}
            >
              {(field?.values ?? []).map((value) => (
                <MenuItem key={String(value)} value={String(value)}>
                  {value === true ? 'yes' : value === false ? 'no' : value}
                </MenuItem>
              ))}
            </TextField>
            <Button disabled={disabled} onClick={() => setFilters(filters.filter((_entry, at) => at !== index))}>
              Remove
            </Button>
          </Stack>
        )
      })}
      <Stack direction="row">
        <Button
          size="small"
          disabled={disabled || tags.length === 0 || fields.length === 0}
          onClick={() => setFilters([...filters, { tag: tags[0] ?? '', where: { field: fields[0]?.name ?? '', equals: '' } }])}
        >
          Add a condition
        </Button>
      </Stack>
    </Stack>
  )
}
